import { 
    Zap, 
    Lock, 
    Gauge, 
    Globe, 
    Layers, 
    RefreshCw,
    CheckCircle,
    ArrowRight
  } from "lucide-react";
  import { Button } from "@/components/ui/button";
  import { useNavigate } from "react-router-dom";
  
  export const FeaturesShowcase = () => {
    const navigate = useNavigate();
    const features = [
      {
        icon: Zap,
        title: "Lightning Fast",
        description: "Sub-100ms response times with our globally distributed edge network.",
      },
      {
        icon: Lock,
        title: "Enterprise Security",
        description: "SOC 2 Type II certified with end-to-end encryption.",
      },
      {
        icon: Gauge,
        title: "99.99% Uptime",
        description: "Industry-leading reliability backed by our SLA guarantee.",
      },
      {
        icon: Globe,
        title: "Global CDN",
        description: "200+ edge locations ensuring low latency worldwide.",
      },
      {
        icon: Layers,
        title: "Instant Deployment",
        description: "Deploy your application in minutes with our cloud team.",
      },
      {
        icon: RefreshCw,
        title: "Auto Scaling",
        description: "Scale from zero to millions of users automatically.",
      },
    ];
  
    return (
      <section id="features" className="py-24 relative overflow-hidden px-4 sm:px-6 lg:px-8 xl:px-12">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        </div>
  
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20 mb-6">
                <Zap className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">Platform Features</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Built for
                <span className="gradient-text"> founders,</span>
                <br />loved by developers
              </h2>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              From mobile apps and web platforms to cloud infrastructure and growth 
              marketing, we handle the complete lifecycle of your digital product — with dedicated developers and 2 years of free support.
              </p>
  
              <div className="space-y-4 mb-8">
                {[
                  "Custom development",
                  "Dedicated development team",
                  "2 years of free support",
                  "Long-term commitment",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
  
              <Button size="lg" className="glow-primary bg-gradient-to-r from-primary to-purple-600 group" onClick={() => navigate("/#features")}>
                View Features
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
  
            {/* Right Grid */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`p-6 rounded-2xl glass-card hover-lift cursor-pointer group ${
                    index === 0 || index === 5 ? "col-span-2 sm:col-span-1" : ""
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 group-hover:scale-110 transition-all">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };